<script setup lang="ts">
import { useGetMultiJobsGeoJson } from '@/api/field-view'
import GeoJsonSource from '@/components/field-view-next/mapbox/GeoJsonSource.vue'
import LayerGroup from '@/components/field-view-next/mapbox/LayerGroup.vue'
import MapHoverPopup from '@/components/field-view-next/mapbox/MapHoverPopup.vue'
import { computed, watch } from 'vue'
import type { GeoJSONFeature, LayerSpecification } from 'mapbox-gl'
import { v4 } from 'uuid'
import { locationPinStyle } from './styles/location-pin'
import { useMap } from '../mapbox/context'
import { useFieldView } from '../FieldViewState'
import {
  buildMultiJobIndexLookup,
  createJobTripLayers,
  formatJobTripGeoJson,
  getMultiJobPaletteByIndex,
} from './jobTripLayers'
import { useMapFitBounds } from '@/components/field-view-next/composables/useMapFitBounds'

const props = defineProps<{
  jobIds: string[]
}>()

const { map, mini } = useMap()
const { setSelectedLocation, setSelectedResource } = useFieldView()
const { fitIfChanged, resetFitKey } = useMapFitBounds()

const maxMultiJobTripMapJobs = 15
const instanceId = v4()
const locationsSourceId = `multi-job-locations-${instanceId}`
const locationsLayerId = `multi-job-locations-layer-${instanceId}`

const getJobSourceId = (jobId?: string | null) => `multi-job-${jobId ?? 'unknown'}`

const latestJobIds = computed(() => props.jobIds.slice(0, maxMultiJobTripMapJobs))
const jobIndexLookup = computed(() => buildMultiJobIndexLookup(latestJobIds.value))
const {
  data,
  isLoading,
  isFetching,
  jobIds: requestedJobIds,
} = useGetMultiJobsGeoJson(latestJobIds)

const showLoading = computed(() => {
  return requestedJobIds.value.length > 0 && (isLoading.value || isFetching.value)
})

const locationsData = computed(() => data.value?.locations ?? null)

const locationLayers = computed<LayerSpecification[]>(() => [
  {
    id: locationsLayerId,
    source: locationsSourceId,
    type: 'symbol',
    layout: {
      ...locationPinStyle.layout,
      'symbol-sort-key': 0,
      'icon-allow-overlap': true,
      'icon-size': 1.5,
      'text-radial-offset': 2,
      'text-allow-overlap': true,
      'text-optional': false,
      'text-size': {
        stops: [
          [15, 11],
          [15.5, 12],
          [17, 14],
        ],
      },
    },
    paint: {
      ...locationPinStyle.paint,
      'text-opacity': 1,
    },
    filter: ['==', ['get', 'Type'], 'Location'],
  },
])

const formattedJobs = computed(() => {
  return (
    data.value?.jobs
      .filter((job): job is NonNullable<typeof data.value>['jobs'][number] => Boolean(job))
      .map((job, index) => {
        const resolvedJobId = job.jobId || `job-${job.jobNumber ?? index}`
        const sourceName = getJobSourceId(resolvedJobId)
        const layerIdPrefix = sourceName
        const requestedJobIndex = job.jobId ? jobIndexLookup.value.get(job.jobId) : undefined
        const palette = getMultiJobPaletteByIndex(requestedJobIndex ?? index)
        const baseGeoJson = formatJobTripGeoJson(job.geoJson) ?? job.geoJson
        const geoJson = {
          ...baseGeoJson,
          features: baseGeoJson.features.map((feature) => ({
            ...feature,
            properties: {
              ...feature.properties,
              JobNumber: job.jobNumber,
            },
          })),
        }

        return {
          ...job,
          resolvedJobId,
          geoJson,
          featureCount: geoJson.features.length,
          debugLayerIds: [
            `${layerIdPrefix}-routes`,
            `${layerIdPrefix}-breadcrumb-circle`,
            `${layerIdPrefix}-start-finish`,
          ],
          layers: createJobTripLayers({
            sourceName,
            layerIdPrefix,
            routeColor: palette.routeColor,
            routeBorderColor: palette.routeBorderColor,
            breadcrumbColor: palette.breadcrumbColor,
            includeResources: true,
          }),
        }
      }) ?? []
  )
})

const getHoverContent = ({ properties }: GeoJSONFeature) => {
  if (!properties) return ''

  const jobNumber = properties.JobNumber

  if (jobNumber) {
    return `Job ${jobNumber}`
  }

  return ''
}

watch(latestJobIds, resetFitKey)
watch(
  [locationsData, formattedJobs, map, mini],
  () => {
    const allCollections = [locationsData.value, ...formattedJobs.value.map((job) => job.geoJson)]

    if (allCollections.every((collection) => !collection || collection.features.length === 0)) {
      return
    }

    try {
      if (fitIfChanged(allCollections, map.value, mini.value)) {
        setSelectedLocation(null)
        setSelectedResource(null)
      }
    } catch {}
  },
  { immediate: true },
)
</script>

<template>
  <div class="multi-jobs-layer">
    <div v-if="showLoading" class="loading-indicator">
      <v-progress-circular color="white" indeterminate size="26" width="3" />
    </div>

    <geo-json-source v-if="locationsData" :data="locationsData" :source-id="locationsSourceId">
      <layer-group :layers="locationLayers" />
    </geo-json-source>

    <template v-for="job in formattedJobs">
      <geo-json-source
        v-if="job.featureCount > 0"
        :key="job.resolvedJobId"
        :data="job.geoJson"
        :source-id="getJobSourceId(job.resolvedJobId)"
        :buffer="2"
        generate-id
      >
        <layer-group :layers="job.layers">
          <template #hover="{ feature }">
            <MapHoverPopup v-if="getHoverContent(feature)" :feature="feature" placement="top">
              <span>{{ getHoverContent(feature) }}</span>
            </MapHoverPopup>
          </template>
        </layer-group>
      </geo-json-source>
    </template>
  </div>
</template>

<style scoped>
.multi-jobs-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.loading-indicator {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.75);
  backdrop-filter: blur(6px);
}
</style>
